import { SpinnerLoader } from '@/components';
import { useTables } from '@/hooks';
import { pxToPdf } from '@/shared/commonFunctions';
import { ItemsProps as ItemsInterface, OrderItemsProps } from '@/talons/orderStatusPage/useOrderStatusPage';
import { CategoryProps, ClientProps } from '@/types';
import { format } from 'date-fns';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LuCircleAlert as AlertLine } from "react-icons/lu";
import ShareOrderHeader from './ShareOrderHeader';
import SharedOrderList from './ShareOrderList';
import ShareOrderPage from './ShareOrderPage';
import * as S from './styles';
import { ReturnBlock } from '@/views/return-report/styles';

const PAGE_WIDTH = 210;
const TITLE_WEIGHT = 0.8;
const ITEMS_PER_PAGE = 14;
interface Props {
  client: ClientProps;
  order: OrderItemsProps;
}

interface ItemsProps extends ItemsInterface {
  INDICE: number;
  CATEGORIA: CategoryProps;
}

const SharedMarketOrder = ({ order, client }: Props) => {
  const { tables } = useTables();
  const printRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(format(new Date(), "dd/MM/yyyy 'às' HH:mm"));

  const isDisabled = useMemo(() => {
    return !order.actionsPending.length || ['processando', 'cancelado', 'faturado', 'entregue'].includes(order.status.toLowerCase()) || order.isLocal;
  }, [order]);

  useEffect(() => {
    const timer = setInterval(() => {
      setDate(format(new Date(), "dd/MM/yyyy 'às' HH:mm"));
    }, 60000); // Atualiza a cada 1 minuto

    return () => clearInterval(timer);
  }, []);

  const categorias = tables.current.dicionario_categorias;

  const products = useMemo(
    () =>
      order.actionsPending.map((i, index) => {
        const item: ItemsProps = i as ItemsProps;
        const findFatherCategory = (category: number) => {
          const itemCategory = categorias[category];
          item.CATEGORIA = itemCategory;
          if (itemCategory?.COD_CATEGORIA_PAI) {
            return findFatherCategory(itemCategory?.COD_CATEGORIA_PAI);
          }
          return item;
        };

        return findFatherCategory(item?.COD_CATEGORIA);
      }) || [],
    [],
  );

  const productsByCategory = useMemo(
    () => {
      const productsByCategory = products.reduce(
        (acc, item) => {
          const category = item.CATEGORIA?.COD_CATEGORIA;
          acc[category] = [...(acc[category] || []), item];
          return acc;
        },
        {} as Record<string, ItemsProps[]>,
      );
      let count = 0;
      const listIndex = Object.entries(productsByCategory).reduce((acc, [key, value]) => {
        value.forEach((item) => {
          count++;
          item.INDICE = count;
        });
        return { ...acc, [key]: value };
      }, {} as Record<number, ItemsProps[]>);
      return listIndex;
    },
    [],
  );

  const categoriesPages = useMemo(() => {
    const pages: Record<number, ItemsProps[]>[] = [];
    let currentPage: Record<number, ItemsProps[]> = {};
    let currentPageWeight = 0;

    Object.entries(productsByCategory).forEach(([categoryKey, items]) => {
      const categoryWeight = TITLE_WEIGHT;

      // Se adicionar o título atual ultrapassar o limite da página, cria uma nova página
      if (currentPageWeight + categoryWeight > ITEMS_PER_PAGE) {
        pages.push(currentPage);
        currentPage = {};
        currentPageWeight = 0;
      }

      // Adiciona o título à página atual
      currentPage[Number(categoryKey)] = [];
      currentPageWeight += categoryWeight;

      // Distribui os itens da categoria
      items.forEach((item) => {
        if (currentPageWeight + 1 > ITEMS_PER_PAGE) {
          pages.push(currentPage);
          currentPage = {};
          currentPageWeight = 0;
          // Adiciona o título novamente na nova página
          currentPage[Number(categoryKey)] = [];
          currentPageWeight += categoryWeight;
        }

        currentPage[Number(categoryKey)] = [...(currentPage[Number(categoryKey)] || []), item];
        currentPageWeight += 1;
      });
    });

    // Adiciona a última página se houver conteúdo
    if (Object.keys(currentPage).length > 0) {
      pages.push(currentPage);
    }

    return pages;
  }, []);

  const generatePDF = useCallback(async () => {
    if (isDisabled) return;

    setLoading(true);
    await new Promise((resolve) => setTimeout(resolve, 1000));

    try {
      if (!printRef.current) return;

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pages = printRef.current.querySelectorAll('.pdf-page');
      
      for (let i = 0; i < pages.length; i++) {
        const canvas = await html2canvas(pages[i] as HTMLElement, {
          scale: 2,
          useCORS: true,
          logging: false,
          allowTaint: true,
          backgroundColor: '#ffffff'
        });

        const imgData = canvas.toDataURL('image/jpeg', 1.0);
        
        if (i > 0) {
          pdf.addPage();
        }

        const imgHeight = (canvas.height * PAGE_WIDTH) / canvas.width;
        
        pdf.addImage(imgData, 'JPEG', 0, 0, PAGE_WIDTH, imgHeight);
      }

      pdf.save(`pedido-${order.orderNumber}.pdf`);
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
    } finally {
      setLoading(false);
    }
  }, [setLoading, isDisabled]);

  return (
    <>
      <S.Content ref={printRef}>
        {categoriesPages.map((page, pageIndex) => (
          <S.PDFPage key={pageIndex} className='pdf-page'>
            <ShareOrderPage position='top' showSite totalPages={categoriesPages.length} sharedDate={date} currentPage={pageIndex + 1} />

            <S.PDFBody>
              <S.Logo src='/marca.png' alt='Marca' />
              <ShareOrderHeader client={client} order={order} />
              <S.BoxPadding>
                <S.AlertBox>
                  <S.Text style={{
                    color: '#F44336',
                    padding: `${pxToPdf(10)} ${pxToPdf(15)}`,
                  }}>
                    <AlertLine size={13} color='#F44336' />
                    Os produtos podem sofrer variação de acordo com o estoque disponível.
                  </S.Text>
                </S.AlertBox>
              </S.BoxPadding>
              <SharedOrderList productsByCategory={page} />
            </S.PDFBody>

            <ShareOrderPage position='bottom' totalPages={categoriesPages.length} sharedDate={date} currentPage={pageIndex + 1} />
          </S.PDFPage>
        ))}
      </S.Content>

      <S.ShareMarketOrder onClick={generatePDF} disabled={loading || isDisabled}>
        {loading ? 'Gerando PDF...' : 'Gerar PDF'} {loading ? <SpinnerLoader /> : <S.ShareMarketOrderIcon />}
      </S.ShareMarketOrder>
    </>
  );
};

export default SharedMarketOrder;
